// PaymentAlertsCard — the student dashboard's outstanding-payments warning.
// Renders nothing when there's nothing to act on — only shown when needed.
import { Link } from "react-router-dom";
import Badge from "../shared/Badge.js";
import { IconAlertTriangle } from "../shared/icons.js";
import { formatKes } from "../../lib/formatMoney.js";
import type { PaymentWithCourse } from "@dm-lms/shared/types";

interface PaymentAlertsCardProps {
  payments: PaymentWithCourse[];
}

export default function PaymentAlertsCard({ payments }: PaymentAlertsCardProps) {
  if (payments.length === 0) return null;

  const failed = payments.filter((p) => p.status === "failed");
  const pending = payments.filter((p) => p.status === "pending");
  const ordered = [...failed, ...pending];
  const title = failed.length > 0 ? "Payment failed" : "Payment required";

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-medium text-amber-800">
        <IconAlertTriangle width={16} height={16} />
        {title}
      </h2>

      <div className="divide-y divide-amber-100">
        {ordered.map((payment) => (
          <div key={payment.id} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm text-dm-navy">{payment.courseTitle ?? payment.code}</p>
              <p className="text-xs text-dm-slate">{formatKes(payment.amountKes)}</p>
            </div>
            <Badge tone={payment.status === "failed" ? "red" : "gold"}>{payment.status}</Badge>
          </div>
        ))}
      </div>

      <Link to="/app/student/payments" className="mt-3 inline-block text-xs font-medium text-dm-blue hover:underline">
        View payments
      </Link>
    </div>
  );
}
